import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import tradingService from '../services/tradingService';
import {isCanceledRequest} from '../services/api';
import ModalShell from '../components/ModalShell';
import useAccountKeys from '../hooks/useAccountKeys';
import AccountTradesSection from '../components/AccountTradesSection';
import AccountBalancesSection from '../components/AccountBalancesSection';
import AccountSubscriptionsSection from '../components/AccountSubscriptionsSection';
import AccountStrategiesSection from '../components/AccountStrategiesSection';
import AccountPerformanceChart from '../components/AccountPerformanceChart';
import {formatPercent, formatUsdt, getPerformanceSummary} from '../utils/accountPerformance';
import './AccountDetailsPage.css';

export default function AccountDetailsPage() {
  const { number } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving] = useState(false);

  const [error, setError] = useState('');

  const [account, setAccount] = useState(null);
  const [exchanges] = useState([]);
  const [valuation, setValuation] = useState({
    initialValue: null,
    currentValue: null,
    changeValue: null,
    changePercent: null,
  });
  const [valuationLoading, setValuationLoading] = useState(false);

  // form state
  const [label, setLabel] = useState('');
  const [exchangeId, setExchangeId] = useState('');
  const [isActive, setIsActive] = useState(true);

  // inline label edit state (pencil)
  const [labelEditing, setLabelEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const [labelSaving, setLabelSaving] = useState(false);
  const [labelSaveError, setLabelSaveError] = useState('');
  const labelInputRef = useRef(null);

  // deactivate confirm modal (one-way toggle)
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  const {
    keysViewOpen,
    setKeysViewOpen,
    keysEditOpen,
    setKeysEditOpen,
    openViewKeys,
    openEditKeys,
    keys,
    loadKeys,
    keysApiKey,
    setKeysApiKey,
    keysApiSecret,
    setKeysApiSecret,
    keysApiPassphrase,
    setKeysApiPassphrase,
    keysLoading,
    keysSaving,
    keysDisabled,
    keysError,
    keysSaveError,
    keysSaveSuccess,
    onSaveKeys,
  } = useAccountKeys(number);

  const exchangesById = useMemo(() => {
    const m = new Map();
    for (const ex of exchanges) m.set(String(ex.id), ex);
    return m;
  }, [exchanges]);

  useEffect(() => {
    if (!number) return;

    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await tradingService.accounts.get({ number, signal: ac.signal });
        const acc = res?.data;
        setAccount(acc);

        setLabel(acc?.label ?? '');
        setLabelDraft(acc?.label ?? '');
        setExchangeId(acc?.exchange?.exchange_id ? String(acc.exchange.exchange_id) : '');
        setIsActive(!!acc?.is_active);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setError(err?.message || 'Failed to load account');
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [number]);

  useEffect(() => {
    if (!number) return undefined;

    const ac = new AbortController();

    (async () => {
      try {
        setValuationLoading(true);

        const res = await tradingService.accounts.performance.history({
          number,
          limit: 5000,
          signal: ac.signal,
        });

        if (ac.signal.aborted) {
          return;
        }

          setValuation(getPerformanceSummary(
              res?.data?.history ?? [],
              res?.data?.initial_balance_usdt,
          ));
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setValuation({
          initialValue: null,
          currentValue: null,
          changeValue: null,
          changePercent: null,
        });
      } finally {
        if (!ac.signal.aborted) {
          setValuationLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [number]);

  useEffect(() => {
    if (labelEditing) {
      const id = window.setTimeout(() => {
        labelInputRef.current?.focus();
        labelInputRef.current?.select?.();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [labelEditing]);

  const saveInlineLabel = async () => {
    if (!number) return;

    const next = (labelDraft ?? '').trim();
    const current = (label ?? '').trim();

    setLabelSaveError('');

    // no-op: just close editor
    if (next === current) {
      setLabelEditing(false);
      setLabelDraft(label ?? '');
      return;
    }

    setLabelSaving(true);
    try {
      const res = await tradingService.accounts.update({
        number,
        label: next,
        exchangeId: String(exchangeId),
        is_active: isActive ? 1 : 0,
      });

      const updated = res?.data;
      setAccount(updated);

      // keep form + draft in sync with server response
      const serverLabel = updated?.label ?? next;
      setLabel(serverLabel);
      setLabelDraft(serverLabel);
      setExchangeId(updated?.exchange?.exchange_id ? String(updated.exchange.exchange_id) : exchangeId);
      setIsActive(!!updated?.is_active);

      setLabelEditing(false);
    } catch (err) {
      setLabelSaveError(err?.message || 'Failed to update label');
      // keep editor open so user can retry / adjust
    } finally {
      setLabelSaving(false);
    }
  };

  const cancelInlineLabel = () => {
    setLabelSaveError('');
    setLabelDraft(label ?? '');
    setLabelEditing(false);
  };

  const confirmDeactivate = async () => {
    if (!number) return;
    if (!isActive) return; // one-way: already inactive, nothing to do

    setDeactivateError('');
    setDeactivating(true);
    try {
      const res = await tradingService.accounts.update({
        number,
        label,
        exchangeId: String(exchangeId),
        is_active: 0,
      });

      const updated = res?.data;
      setAccount(updated);

      // keep form in sync with server response
      setLabel(updated?.label ?? label ?? '');
      setLabelDraft(updated?.label ?? labelDraft ?? '');
      setExchangeId(updated?.exchange?.exchange_id ? String(updated.exchange.exchange_id) : exchangeId);
      setIsActive(false);

      setDeactivateOpen(false);
    } catch (err) {
      setDeactivateError(err?.message || 'Failed to deactivate account');
    } finally {
      setDeactivating(false);
    }
  };

  const exchangeName =
    account?.exchange?.exchange_name ??
    exchangesById.get(String(account?.exchange?.exchange_id))?.exchange_name ??
    '—';

  const strategiesActive = Number(account?.strategies?.active ?? 0);
  const strategiesTotal = Number(account?.strategies?.total ?? 0);
  const subsActive = Number(account?.subscriptions?.active ?? 0);
  const subsTotal = Number(account?.subscriptions?.total ?? 0);

  const formatActiveTotal = (active, total) => `${Number(active ?? 0)} / ${Number(total ?? 0)}`;

  return (
    <div className="card">
      <div className="account-details__header">
        <div>
          <h1 className="account-details__title">Account</h1>
        </div>

        <div className="account-details__actions">
          <button
            type="button"
            className="account-details__btn account-details__btn--primary"
            onClick={openViewKeys}
            disabled={!number || loading}
          >
            View keys
          </button>
          <button type="button" className="account-details__btn" onClick={() => navigate('/my/accounts')}>
            Back
          </button>
        </div>
      </div>

      {error ? <p className="account-details__error">{error}</p> : null}
      {loading ? <p>Loading…</p> : null}

      {!loading && account ? (
        <>
          <section className="account-details__summary">
            <div className="account-details__summary-top">
              <div>
                <div className="account-details__summary-kicker">Overview</div>

                <div className="account-details__summary-title-row">
                  {!labelEditing ? (
                    <>
                      <div className="account-details__summary-title" title={label || '—'}>
                        {label || '—'}
                      </div>

                      <button
                        type="button"
                        className="account-details__icon-btn"
                        aria-label="Edit label"
                        title="Edit label"
                        onClick={() => {
                          setLabelSaveError('');
                          setLabelDraft(label ?? '');
                          setLabelEditing(true);
                        }}
                        disabled={labelSaving || saving}
                      >
                        ✎
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        ref={labelInputRef}
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                        onBlur={saveInlineLabel}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveInlineLabel();
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelInlineLabel();
                          }
                        }}
                        className="account-details__inline-input"
                        placeholder="Label…"
                        disabled={labelSaving}
                      />
                      <span className="account-details__inline-hint">
                        {labelSaving ? 'Saving…' : 'Enter to save · Esc to cancel'}
                      </span>
                    </>
                  )}
                </div>

                {labelSaveError ? <div className="account-details__save-error">{labelSaveError}</div> : null}
              </div>

              <div className="account-details__status-row">
                <label
                  className={`account-details__toggle ${isActive ? '' : 'is-disabled'}`}
                  title={isActive ? 'Deactivate account' : 'Account is inactive (cannot be reactivated)'}
                >
                  <input
                    type="checkbox"
                    checked={isActive}
                    disabled={!isActive || saving || labelSaving || deactivating}
                    onChange={() => {
                      // one-way switch: only allow Active -> Inactive
                      if (!isActive) return;
                      setDeactivateError('');
                      setDeactivateOpen(true);
                    }}
                    aria-label="Account active status"
                  />
                  <span className="account-details__toggle-ui" aria-hidden="true" />
                  <span className="account-details__toggle-text">{isActive ? 'Active' : 'Inactive'}</span>
                </label>
              </div>
            </div>

            <div className="account-details__summary-grid">
              <div className="account-details__kv">
                <div className="account-details__kv-k">Exchange</div>
                <div className="account-details__kv-v" title={exchangeName}>
                  {exchangeName}
                </div>
              </div>

              <div className="account-details__kv">
                <div className="account-details__kv-k">Strategies</div>
                <div
                  className="account-details__kv-v"
                  title={`Active / Total: ${formatActiveTotal(strategiesActive, strategiesTotal)}`}
                >
                  {formatActiveTotal(strategiesActive, strategiesTotal)}
                </div>
              </div>

              <div className="account-details__kv">
                <div className="account-details__kv-k">Subscriptions</div>
                <div
                  className="account-details__kv-v"
                  title={`Active / Total: ${formatActiveTotal(subsActive, subsTotal)}`}
                >
                  {formatActiveTotal(subsActive, subsTotal)}
                </div>
              </div>
            </div>

            <div className="account-details__valuation-grid">
              <div className="account-details__kv">
                <div className="account-details__kv-k">Initial valuation</div>
                <div className="account-details__kv-v" title={formatUsdt(valuation.initialValue)}>
                  {valuationLoading ? 'Loading…' : formatUsdt(valuation.initialValue)}
                </div>
              </div>

              <div className="account-details__kv">
                <div className="account-details__kv-k">Current valuation</div>
                <div className="account-details__kv-v" title={formatUsdt(valuation.currentValue)}>
                  {valuationLoading ? 'Loading…' : formatUsdt(valuation.currentValue)}
                </div>
              </div>

              <div className="account-details__kv">
                <div className="account-details__kv-k">% Change</div>
                <div
                  className={`account-details__kv-v ${
                    Number(valuation.changePercent) > 0
                      ? 'account-details__kv-v--positive'
                      : Number(valuation.changePercent) < 0
                        ? 'account-details__kv-v--negative'
                        : ''
                  }`}
                  title={formatPercent(valuation.changePercent)}
                >
                  {valuationLoading ? 'Loading…' : formatPercent(valuation.changePercent)}
                </div>
              </div>
            </div>
          </section>

          <AccountStrategiesSection number={number} />
          <AccountSubscriptionsSection number={number} />
          <AccountTradesSection number={number} />
          <AccountBalancesSection number={number} />
          <AccountPerformanceChart number={number} />
        </>
      ) : null}

      <ModalShell
        title="Confirm deactivation"
        open={deactivateOpen}
        onClose={() => {
          if (deactivating) return;
          setDeactivateOpen(false);
          setDeactivateError('');
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setDeactivateOpen(false);
                setDeactivateError('');
              }}
              disabled={deactivating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="account-details__btn account-details__btn--danger"
              onClick={confirmDeactivate}
              disabled={deactivating}
            >
              {deactivating ? 'Deactivating…' : 'Deactivate'}
            </button>
          </>
        }
      >
        <p>
          This will set the account to <strong>Inactive</strong>. This action cannot be undone.
        </p>
        {deactivateError ? <div className="account-details__save-error">{deactivateError}</div> : null}
      </ModalShell>

      <ModalShell
        title="API keys (view)"
        open={keysViewOpen}
        onClose={() => setKeysViewOpen(false)}
        footer={
          <>
            <button type="button" onClick={() => setKeysViewOpen(false)}>
              Done
            </button>
          </>
        }
      >
        {keysError ? <div className="account-details__keys-error">{keysError}</div> : null}
        {keysLoading ? <div>Loading…</div> : null}

        {!keysLoading ? (
          <div className="account-details__keys-grid">
            <div className="account-details__keys-row">
              <div>
                <div className="account-details__field-title">API key</div>
                <code>{keys.public_key}</code>
              </div>

              <div>
                <div className="account-details__field-title">API secret</div>
                <code>{keys.secret_key}</code>
              </div>

              <div>
                <div className="account-details__field-title">API passphrase</div>
                <code>{keys.trading_password}</code>
              </div>

              <div className="account-details__keys-actions">
                <button type="button" onClick={openEditKeys}>
                  Update keys…
                </button>
                <button type="button" onClick={loadKeys} disabled={keysDisabled}>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </ModalShell>

      <ModalShell
        title="API keys (update)"
        open={keysEditOpen}
        onClose={() => setKeysEditOpen(false)}
        footer={
          <>
            <button type="button" onClick={() => setKeysEditOpen(false)} disabled={keysSaving}>
              Close
            </button>
            <button type="submit" form="keys-edit-form" disabled={keysDisabled}>
              {keysSaving ? 'Saving…' : 'Save keys'}
            </button>
          </>
        }
      >
        {keysError ? <div className="account-details__keys-error">{keysError}</div> : null}
        {keysSaveError ? <div className="account-details__keys-error">{keysSaveError}</div> : null}
        {keysSaveSuccess ? <div className="account-details__keys-success">{keysSaveSuccess}</div> : null}

        <form id="keys-edit-form" onSubmit={onSaveKeys} className="account-details__keys-form">
          <div className="account-details__keys-tip">Tip: leaving a field blank will save it as blank (i.e., clear it).</div>

          <label>
            <div className="account-details__field-title">API key</div>
            <input
              value={keysApiKey}
              onChange={(e) => setKeysApiKey(e.target.value)}
              disabled={keysDisabled}
              autoComplete="off"
              className="account-details__control"
            />
          </label>

          <label>
            <div className="account-details__field-title">API secret</div>
            <input
              value={keysApiSecret}
              onChange={(e) => setKeysApiSecret(e.target.value)}
              disabled={keysDisabled}
              autoComplete="off"
              className="account-details__control"
            />
          </label>

          <label>
            <div className="account-details__field-title">API passphrase</div>
            <input
              value={keysApiPassphrase}
              onChange={(e) => setKeysApiPassphrase(e.target.value)}
              disabled={keysDisabled}
              autoComplete="off"
              className="account-details__control"
            />
          </label>

          <div className="account-details__keys-actions">
            <button type="button" onClick={loadKeys} disabled={keysDisabled}>
              Reload from server
            </button>
          </div>
        </form>
      </ModalShell>
    </div>
  );
}
